import { Collaboration, Room } from "src/app/models/Chat.v2";
import { getClientId, getUserId } from "src/app/modules/localstorage";
import { CHAT_LIST_PAGE_SIZE } from "../config/queryLimits";
import {
  buildChatCacheKey,
  capItems,
  deserializeTimestamp,
  getTimestampMillis,
  readChatCache,
  serializeTimestamp,
  writeChatCache,
  type StoredTimestamp,
} from "./chatStorageCache";

type ChatType = "allChat" | "myChat";

type StoredChatList = {
  collaborations: Array<Record<string, any>>;
  newestKnownLastInteractionAt: StoredTimestamp | null;
  oldestVisibleLastInteractionAt: StoredTimestamp | null;
  fetchedAt: number;
};

const COLLAB_TIMESTAMP_FIELDS = [
  "lastInteractionAt",
  "lastUserInteractionAt",
  "createdAt",
  "updatedAt",
  "handleAt",
] as const;

const ROOM_TIMESTAMP_FIELDS = [
  "createdAt",
  "updatedAt",
  "lastCustomerInteractionAt",
] as const;

export const buildChatListStorageCacheKey = (chatType: ChatType) => {
  const clientId = getClientId();
  const userId = getUserId();

  if (!clientId || !userId) return null;
  return buildChatCacheKey(clientId, userId, "chat-list", chatType);
};

const serializeRef = (value: any) => {
  if (!value || typeof value !== "object") return value;
  return { id: value.id, path: value.path };
};

const serializeRoom = (room: Room) => {
  const serialized: Record<string, any> = {
    ...room,
    account: serializeRef(room.account),
    client: serializeRef(room.client),
    toUser: room.toUser?.map(serializeRef),
    toUserOnline: room.toUserOnline?.map(serializeRef),
  };

  ROOM_TIMESTAMP_FIELDS.forEach((field) => {
    serialized[field] = serializeTimestamp(room[field]);
  });

  return serialized;
};

const hydrateRoom = (room: Record<string, any>) => {
  const hydrated = { ...room };

  ROOM_TIMESTAMP_FIELDS.forEach((field) => {
    hydrated[field] = deserializeTimestamp(room[field]);
  });

  return hydrated as Room;
};

const serializeCollaboration = (collab: Collaboration) => {
  const serialized: Record<string, any> = {
    ...collab,
    account: serializeRef(collab.account),
    client: serializeRef(collab.client),
    customer: serializeRef(collab.customer),
    handledByUser: serializeRef(collab.handledByUser),
    company: serializeRef(collab.company),
    toUser: collab.toUser?.map(serializeRef),
    joinedUsers: collab.joinedUsers?.map(serializeRef),
    roomsModel: collab.roomsModel?.map(serializeRoom),
  };

  COLLAB_TIMESTAMP_FIELDS.forEach((field) => {
    serialized[field] = serializeTimestamp(collab[field]);
  });

  return serialized;
};

const hydrateCollaboration = (collab: Record<string, any>) => {
  const hydrated = {
    ...collab,
    roomsModel: collab.roomsModel?.map(hydrateRoom),
  };

  COLLAB_TIMESTAMP_FIELDS.forEach((field) => {
    hydrated[field] = deserializeTimestamp(collab[field]);
  });

  return hydrated as Collaboration;
};

const sortByLastInteractionDesc = (items: Collaboration[]) => {
  return [...items].sort(
    (a, b) =>
      (b.lastInteractionAt?.seconds ?? 0) -
      (a.lastInteractionAt?.seconds ?? 0)
  );
};

export const getChatListCursorFromData = (collaborations: Collaboration[]) => {
  const sorted = sortByLastInteractionDesc(collaborations);

  return {
    newestKnownLastInteractionAt: sorted[0]?.lastInteractionAt ?? null,
    oldestVisibleLastInteractionAt:
      sorted[sorted.length - 1]?.lastInteractionAt ?? null,
  };
};

export const readStoredChatList = (chatType: ChatType) => {
  const key = buildChatListStorageCacheKey(chatType);
  if (!key) return null;

  const cached = readChatCache<StoredChatList>(key);
  if (!cached?.collaborations?.length) return null;

  return {
    collaborations: cached.collaborations.map(hydrateCollaboration),
    newestKnownLastInteractionAt: deserializeTimestamp(
      cached.newestKnownLastInteractionAt
    ),
    oldestVisibleLastInteractionAt: deserializeTimestamp(
      cached.oldestVisibleLastInteractionAt
    ),
    fetchedAt: cached.fetchedAt,
  };
};

export const writeStoredChatList = (
  chatType: ChatType,
  collaborations: Collaboration[]
) => {
  const key = buildChatListStorageCacheKey(chatType);
  if (!key) return false;

  const capped = capItems(sortByLastInteractionDesc(collaborations), CHAT_LIST_PAGE_SIZE);
  const cursor = getChatListCursorFromData(capped);

  return writeChatCache<StoredChatList>(key, {
    collaborations: capped.map(serializeCollaboration),
    newestKnownLastInteractionAt: serializeTimestamp(
      cursor.newestKnownLastInteractionAt
    ),
    oldestVisibleLastInteractionAt: serializeTimestamp(
      cursor.oldestVisibleLastInteractionAt
    ),
    fetchedAt: Date.now(),
  });
};

export const mergeChatListForStorage = (
  existing: Collaboration[],
  incoming: Collaboration[]
) => {
  const byId = new Map(existing.map((collaboration) => [collaboration.id, collaboration]));

  incoming.forEach((next) => {
    const current = byId.get(next.id);
    if (!current || isCollaborationNewer(next, current)) {
      byId.set(next.id, {
        ...current,
        ...next,
        roomsModel: next.roomsModel ?? current?.roomsModel,
      });
    }
  });

  return capItems(
    sortByLastInteractionDesc(Array.from(byId.values())),
    CHAT_LIST_PAGE_SIZE
  );
};

const isCollaborationNewer = (
  next: Collaboration,
  current: Collaboration
) => {
  const nextUpdatedAt = getTimestampMillis(next.updatedAt);
  const currentUpdatedAt = getTimestampMillis(current.updatedAt);

  if (nextUpdatedAt !== null && currentUpdatedAt !== null) {
    return nextUpdatedAt > currentUpdatedAt;
  }

  return (getTimestampMillis(next.lastInteractionAt) ?? 0) >
    (getTimestampMillis(current.lastInteractionAt) ?? 0);
};

export const updateStoredChatListCollaboration = (
  chatType: ChatType,
  collaborationId: string,
  update: (collaboration: Collaboration) => Collaboration
) => {
  const cached = readStoredChatList(chatType);
  if (!cached) return false;

  const updated = cached.collaborations.map((collaboration) =>
    collaboration.id === collaborationId ? update(collaboration) : collaboration
  );

  return writeStoredChatList(chatType, updated);
};

export const applyStoredChatListCollaboration = (
  chatType: ChatType,
  collaboration: Collaboration
) => {
  const cached = readStoredChatList(chatType);
  if (!cached) return false;

  return writeStoredChatList(
    chatType,
    mergeChatListForStorage(cached.collaborations, [collaboration])
  );
};

export const removeStoredChatListCollaboration = (
  chatType: ChatType,
  collaborationId: string
) => {
  const cached = readStoredChatList(chatType);
  if (!cached) return false;

  return writeStoredChatList(
    chatType,
    cached.collaborations.filter((collaboration) => collaboration.id !== collaborationId)
  );
};

export const resetStoredRoomUnreadCount = (
  chatType: ChatType,
  collaborationId: string,
  roomId: string
) =>
  updateStoredChatListCollaboration(chatType, collaborationId, (collaboration) => {
    const room = collaboration.roomsModel?.find((item) => item.id === roomId);
    if (!room) return collaboration;

    const roomUnreadCount = Math.max(0, room.unreadCount ?? 0);
    return {
      ...collaboration,
      unreadCount: Math.max(0, (collaboration.unreadCount ?? 0) - roomUnreadCount),
      roomsModel: collaboration.roomsModel?.map((item) =>
        item.id === roomId ? { ...item, unreadCount: 0 } : item
      ),
    };
  });
